/**
 * KanbanColumnPathPicker — UI выбора цепочки колонок справочников для
 * канбан-группировки (todos.md #5). Используется в EditViewModal/CreateViewModal,
 * когда поле-колонка канбана — reference-поле.
 *
 * Поведение зеркалит ReferenceAttributeColumnModal: загружает справочник
 * выбранного reference-поля, затем для каждой выбранной reference-колонки
 * подгружает следующий уровень. Глубина — до REFERENCE_ATTRIBUTE_MAX_DEPTH.
 *
 * Если path пустой — группировка идёт по самому reference-полю (старое
 * поведение). Чтобы убрать выбранную цепочку, есть кнопка «Сбросить».
 */
import { useEffect, useState } from 'react';
import { Alert, Breadcrumb, Button, Select, Space, Spin } from 'antd';
import type { DictionaryColumn, FieldDefinition } from '@lens/shared';
import { REFERENCE_ATTRIBUTE_MAX_DEPTH } from '@lens/shared';
import { getDictionary } from '../dictionaries/api.ts';

interface PathLevel {
  dictionaryId: string;
  dictionaryName: string;
  columns: DictionaryColumn[];
  selected: string | null;
}

export interface KanbanColumnPathPickerProps {
  /** Текущее reference-поле (типа `reference`). Если другого типа — компонент рендерит null. */
  referenceField: FieldDefinition | undefined;
  /** Текущее значение path (slug'и колонок справочников). */
  value: string[];
  onChange: (path: string[]) => void;
}

export function KanbanColumnPathPicker({
  referenceField,
  value,
  onChange,
}: KanbanColumnPathPickerProps): JSX.Element | null {
  const [levels, setLevels] = useState<PathLevel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Перезагружаем цепочку с нуля при смене reference-поля или value (например, после сброса).
  useEffect(() => {
    if (!referenceField || referenceField.fieldType !== 'reference') {
      setLevels([]);
      return;
    }
    const rootDictId = (referenceField.config as { dictionaryId?: string } | null)?.dictionaryId;
    if (!rootDictId) {
      setLevels([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const root = await getDictionary(rootDictId);
        if (cancelled) return;
        const initial: PathLevel[] = [
          {
            dictionaryId: rootDictId,
            dictionaryName: root.name,
            columns: (root.columns ?? []) as DictionaryColumn[],
            selected: value[0] ?? null,
          },
        ];
        // Прокручиваем сохранённый path, докачивая промежуточные справочники.
        let currentColumns = (root.columns ?? []) as DictionaryColumn[];
        for (let i = 0; i < value.length; i += 1) {
          const slug = value[i];
          if (!slug) break;
          const col = currentColumns.find((c) => c.slug === slug);
          if (!col) break;
          if (i === value.length - 1) break;
          if (col.columnType !== 'reference') break;
          const nextDictId = (col.config as { dictionaryId?: string } | null)?.dictionaryId;
          if (!nextDictId) break;
          // eslint-disable-next-line no-await-in-loop
          const nextDict = await getDictionary(nextDictId);
          if (cancelled) return;
          const nextColumns = (nextDict.columns ?? []) as DictionaryColumn[];
          initial.push({
            dictionaryId: nextDictId,
            dictionaryName: nextDict.name,
            columns: nextColumns,
            selected: value[i + 1] ?? null,
          });
          currentColumns = nextColumns;
        }
        if (!cancelled) setLevels(initial);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Не удалось загрузить справочник');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // value по ссылке меняется при каждом онченже, чтобы не зацикливать — сравниваем по строке
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceField?.slug, value.join('|')]);

  if (!referenceField || referenceField.fieldType !== 'reference') return null;

  const handleSelectAt = async (levelIndex: number, slug: string) => {
    const level = levels[levelIndex];
    if (!level) return;
    const col = level.columns.find((c) => c.slug === slug);
    if (!col) return;

    const newPath = [...value.slice(0, levelIndex), slug];

    // Обрезаем уровни справа.
    setLevels((prev) =>
      prev.slice(0, levelIndex + 1).map((l, i) => (i === levelIndex ? { ...l, selected: slug } : l)),
    );

    onChange(newPath);

    const nextDepth = levelIndex + 1;
    if (col.columnType === 'reference' && nextDepth < REFERENCE_ATTRIBUTE_MAX_DEPTH) {
      const nextDictId = (col.config as { dictionaryId?: string } | null)?.dictionaryId;
      if (!nextDictId) return;
      setLoading(true);
      setError(null);
      try {
        const dict = await getDictionary(nextDictId);
        setLevels((prev) => {
          if (prev.length !== nextDepth) return prev;
          return [
            ...prev,
            {
              dictionaryId: nextDictId,
              dictionaryName: dict.name,
              columns: (dict.columns ?? []) as DictionaryColumn[],
              selected: null,
            },
          ];
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Не удалось загрузить справочник');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReset = () => {
    onChange([]);
  };

  return (
    <div>
      {error && <Alert type="error" message={error} style={{ marginBottom: 8 }} />}
      {levels.length > 0 && value.length > 0 && (
        <Breadcrumb
          style={{ marginBottom: 8 }}
          items={(() => {
            const crumbs: Array<{ title: string }> = [];
            for (let i = 0; i < levels.length; i += 1) {
              const lvl = levels[i];
              if (!lvl) continue;
              crumbs.push({ title: lvl.dictionaryName });
              if (lvl.selected) {
                const col = lvl.columns.find((c) => c.slug === lvl.selected);
                crumbs.push({ title: col?.name ?? lvl.selected });
              }
            }
            return crumbs;
          })()}
        />
      )}
      <Space direction="vertical" style={{ width: '100%' }} size={6}>
        {levels.map((level, idx) => (
          <Select
            key={`${level.dictionaryId}-${idx}`}
            size="small"
            value={level.selected ?? undefined}
            onChange={(v) => void handleSelectAt(idx, v)}
            placeholder={`Колонка из «${level.dictionaryName}»`}
            style={{ width: '100%' }}
            options={level.columns.map((c) => ({
              value: c.slug,
              label: c.columnType === 'reference' ? `${c.name} →` : c.name,
            }))}
            showSearch
            optionFilterProp="label"
          />
        ))}
        {loading && <Spin size="small" />}
        {value.length > 0 && (
          <Button size="small" type="link" onClick={handleReset} style={{ paddingLeft: 0 }}>
            Группировать по самому полю (сбросить цепочку)
          </Button>
        )}
        {levels.length >= REFERENCE_ATTRIBUTE_MAX_DEPTH && (
          <Alert
            type="info"
            showIcon
            message={`Достигнут лимит глубины (${REFERENCE_ATTRIBUTE_MAX_DEPTH} уровней).`}
          />
        )}
      </Space>
    </div>
  );
}
